<script setup>
import { useDisplay } from 'vuetify'

definePage({
  meta: {
    layout: 'docs',
    title: 'Documentation',
  },
})

const route = useRoute()
const router = useRouter()
const { mdAndUp } = useDisplay()

const markdownFiles = import.meta.glob('@/assets/docs/**/*.md', {
  query: '?raw',
  import: 'default',
  eager: true,
})

const headings = ref([])
const frontmatter = ref({})

const rawMarkdown = computed(() => {
  const slug = route.params.slug

  if (!slug || slug.length === 0) {
    router.replace('/documentation/feedback/features/01-overview')

    return ''
  }

  const slugPath = Array.isArray(slug) ? slug.join('/') : slug
  const filePath = `/src/assets/docs/${slugPath}.md`
  const content = markdownFiles[filePath]

  if (!content) {
    return '# Page Not Found\n\nThe requested documentation page does not exist.'
  }

  return content
})

watch(
  frontmatter,
  val => {
    if (val?.title) {
      document.title = `${val.title} — Documentation`
    }
  },
)

// Handle hash scroll on initial page load
onMounted(() => {
  const hash = route.hash
  if (hash) {
    setTimeout(() => {
      const el = document.getElementById(hash.slice(1))
      if (el)
        el.scrollIntoView({ behavior: 'smooth', block: 'start' })
    }, 300)
  }
})

// Mobile sub-header
const toggleMobileDrawer = inject('toggleMobileDrawer', () => {})
const isTocOpen = ref(false)

const scrollToHeading = id => {
  isTocOpen.value = false
  nextTick(() => {
    const el = document.getElementById(id)
    if (el)
      el.scrollIntoView({ behavior: 'smooth', block: 'start' })
  })
}

const scrollToTop = () => {
  isTocOpen.value = false
  window.scrollTo({ top: 0, behavior: 'smooth' })
}
</script>

<template>
  <div>
    <!-- Mobile sub-header bar -->
    <div
      v-if="!mdAndUp"
      class="mobile-sub-header d-flex align-center justify-space-between"
    >
      <!-- Menu button (opens sidebar) -->
      <button
        class="mobile-sub-btn d-flex align-center gap-2"
        @click="toggleMobileDrawer"
      >
        <VIcon
          icon="tabler-menu-2"
          size="18"
        />
        <span class="text-body-2 font-weight-medium">Menu</span>
      </button>

      <!-- On this page dropdown -->
      <div class="mobile-toc-wrapper">
        <button
          class="mobile-sub-btn d-flex align-center gap-1"
          @click="isTocOpen = !isTocOpen"
        >
          <span class="text-body-2 font-weight-medium">On this page</span>
          <VIcon
            :icon="isTocOpen ? 'tabler-chevron-down' : 'tabler-chevron-right'"
            size="16"
          />
        </button>

        <!-- TOC dropdown -->
        <Transition name="toc-dropdown">
          <div
            v-if="isTocOpen && headings.length"
            class="mobile-toc-dropdown"
          >
            <button
              class="mobile-toc-item mobile-toc-item--top text-primary"
              @click="scrollToTop"
            >
              Return to top
            </button>
            <VDivider class="my-1" />
            <button
              v-for="heading in headings"
              :key="heading.id"
              class="mobile-toc-item"
              :class="{ 'mobile-toc-item--h3': heading.level === 3 }"
              @click="scrollToHeading(heading.id)"
            >
              {{ heading.text }}
            </button>
          </div>
        </Transition>
      </div>
    </div>

    <!-- Main content area -->
    <div class="docs-page d-flex">
      <div class="docs-content-area flex-grow-1">
        <DocsContent
          :raw-markdown="rawMarkdown"
          @update:headings="headings = $event"
          @update:frontmatter="frontmatter = $event"
        />
      </div>
      <DocsToc :headings="headings" />
    </div>
  </div>
</template>

<style scoped>
.docs-page {
  min-block-size: calc(100vh - 64px);
}

.docs-content-area {
  min-inline-size: 0;
  overflow-x: hidden;
}

/* ── Mobile sub-header ─────────────────────────────────────────── */
.mobile-sub-header {
  position: sticky;
  inset-block-start: 64px;
  z-index: 10;
  padding-block: 8px;
  padding-inline: 16px;
  background: rgb(var(--v-theme-surface));
  border-block-end: 1px solid rgba(var(--v-border-color), var(--v-border-opacity));
}

.mobile-sub-btn {
  border: none;
  background: none;
  cursor: pointer;
  padding-block: 6px;
  padding-inline: 4px;
  color: rgba(var(--v-theme-on-surface), var(--v-high-emphasis-opacity));
  transition: color 0.15s ease;
}

.mobile-sub-btn:hover {
  color: rgb(var(--v-theme-primary));
}

/* ── Mobile TOC dropdown ───────────────────────────────────────── */
.mobile-toc-wrapper {
  position: relative;
}

.mobile-toc-dropdown {
  position: absolute;
  inset-inline-end: 0;
  inset-block-start: 100%;
  margin-block-start: 8px;
  inline-size: max-content;
  min-inline-size: 220px;
  max-inline-size: calc(100vw - 32px);
  max-block-size: 50vh;
  overflow-y: auto;
  padding-block: 8px;
  background: rgb(var(--v-theme-surface));
  border: 1px solid rgba(var(--v-border-color), var(--v-border-opacity));
  border-radius: 8px;
  box-shadow: 0 8px 24px rgba(0, 0, 0, 0.12);
}

.mobile-toc-item {
  display: block;
  inline-size: 100%;
  padding-block: 8px;
  padding-inline: 16px;
  border: none;
  background: none;
  cursor: pointer;
  text-align: start;
  font-size: 0.875rem;
  color: rgba(var(--v-theme-on-surface), var(--v-medium-emphasis-opacity));
  transition: all 0.1s ease;
}

.mobile-toc-item:hover {
  color: rgba(var(--v-theme-on-surface), var(--v-high-emphasis-opacity));
  background: rgba(var(--v-theme-on-surface), 0.04);
}

.mobile-toc-item--top {
  font-weight: 500;
}

.mobile-toc-item--h3 {
  padding-inline-start: 32px;
  font-size: 0.8125rem;
}

/* ── TOC dropdown transition ───────────────────────────────────── */
.toc-dropdown-enter-active,
.toc-dropdown-leave-active {
  transition: opacity 0.15s ease, transform 0.15s ease;
}

.toc-dropdown-enter-from,
.toc-dropdown-leave-to {
  opacity: 0;
  transform: translateY(-4px);
}
</style>
